import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Checkbox,
  Button,
  Spinner,
  Card,
  CardBody,
  useColorMode,
  Divider,
} from "@chakra-ui/react";
import { getColor, getComponent } from "../../brandConfig";
import { PUBLIC_LEAD_FLOW_FORM, SUBMIT_LEAD } from "./queries";
import { HelpTip } from "./components/HelpTip";
import RewardCelebration from "./components/RewardCelebration";
import { LeadFlowFormDisplayStyle } from "../../generated/graphql";

// Standard Lead fields that map directly (not to customFields)
const STANDARD_LEAD_FIELDS = ["firstName", "lastName", "email", "phone"];

interface PublicFormProps {
  slug?: string;
  embed?: boolean; // When true, skip the Container + Card chrome so the form blends into the surrounding page (e.g. hero section).
  /** Override the default /congrats redirect. Pass null to disable redirect (render inline success state). */
  redirectTo?: string | null;
  /**
   * Opt-in for anonymous-friendly forms (e.g. a public feedback survey with no
   * name/email fields). When true, a blank name/email is backfilled with safe
   * placeholders so the Lead backend (which requires firstName + a valid email)
   * accepts the submission. Default false — identity-capturing forms (waitlists)
   * keep requiring real values.
   */
  anonymous?: boolean;
}

const PublicForm: React.FC<PublicFormProps> = ({
  slug: slugProp,
  embed = false,
  redirectTo = "/congrats",
  anonymous = false,
}) => {
  const params = useParams<{ slug: string }>();
  const slug = slugProp || params.slug;
  const { colorMode } = useColorMode();
  const navigate = useNavigate();

  const { data, loading, error } = useQuery(PUBLIC_LEAD_FLOW_FORM, {
    variables: { slug },
    skip: !slug,
  });

  const [submitLead, { loading: submitting }] = useMutation(SUBMIT_LEAD);

  const [formData, setFormData] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [rewardAward, setRewardAward] = useState<{ count: number; tokenName: string } | null>(
    null
  );

  const form = data?.publicLeadFlowForm;

  // Sort fields by order and group by order ranges
  const sortedFields = useMemo(() => {
    if (!form?.fields) return [];
    return [...form.fields].sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
  }, [form?.fields]);

  // Auto-anonymous: the backend SubmitLeadInput REQUIRES a non-empty firstName
  // and a valid email. A feedback-style form (like the Spring Menu survey) may
  // define neither field. When the form definition omits an email field, treat
  // it as anonymous and backfill safe placeholders on submit — otherwise the
  // standalone /forms/:slug and /form/:slug routes would post a blank email and
  // the mutation would reject it. The explicit `anonymous` prop still forces it
  // on for embeds that want it regardless of field composition.
  const formHasEmailField = useMemo(
    () => sortedFields.some((f: any) => f.name === "email" || f.type === "email"),
    [sortedFields]
  );
  const treatAsAnonymous = anonymous || !formHasEmailField;
  const isEasyRead = form?.displayStyle === LeadFlowFormDisplayStyle.EasyRead;

  // Detect section breaks (gaps of 5+ in order numbers)
  const fieldGroups = useMemo(() => {
    if (!sortedFields.length) return [];
    const groups: { fields: any[] }[] = [{ fields: [] }];
    let lastOrder = sortedFields[0]?.order || 0;

    for (const field of sortedFields) {
      const currentOrder = field.order || 0;
      if (currentOrder - lastOrder >= 5 && groups[groups.length - 1].fields.length > 0) {
        groups.push({ fields: [] });
      }
      groups[groups.length - 1].fields.push(field);
      lastOrder = currentOrder;
    }
    return groups;
  }, [sortedFields]);

  const handleChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    // Separate standard Lead fields from custom fields
    const standardData: Record<string, string> = {};
    const customFields: Record<string, string> = {};

    for (const [key, value] of Object.entries(formData)) {
      if (STANDARD_LEAD_FIELDS.includes(key)) {
        standardData[key] = value;
      } else {
        customFields[key] = value;
      }
    }

    // Anonymous forms (no name/email fields) backfill safe placeholders so the
    // Lead backend accepts them. Identity forms keep whatever the visitor typed.
    const submitFirstName = standardData.firstName || (treatAsAnonymous ? "Anonymous" : "");
    const submitEmail =
      standardData.email ||
      (treatAsAnonymous ? "no-reply@paradiselakescarecentre.com.au" : "");

    try {
      const { data: submitData } = await submitLead({
        variables: {
          input: {
            firstName: submitFirstName,
            lastName: standardData.lastName || "",
            email: submitEmail,
            phone: standardData.phone || "",
            formId: form?.id,
            customFields: Object.keys(customFields).length > 0 ? customFields : undefined,
          },
        },
      });
      setSubmitted(true);

      // If the form awarded reward tokens, keep the user here ~4s to see the
      // celebration, THEN redirect (if redirect is set). Otherwise redirect
      // immediately.
      const awarded = submitData?.submitLead?.tokensAwarded;
      const tokenName = submitData?.submitLead?.tokenName;
      if (awarded && awarded > 0 && tokenName) {
        setRewardAward({ count: awarded, tokenName });
      }

      if (redirectTo) {
        const qs = standardData.email
          ? `?email=${encodeURIComponent(standardData.email)}`
          : "";
        const delay = awarded && awarded > 0 ? 4000 : 0;
        setTimeout(() => navigate(`${redirectTo}${qs}`), delay);
      }
    } catch (err: any) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
    }
  };

  // Easy Read is a public, resident-facing style with NO light/dark toggle, so
  // it uses a fixed high-contrast light palette (dark text on white, strong
  // visible field borders) rather than the theme tokens. Generic keeps the
  // theme-driven colours. NOTE: the card colour uses `background.cardGradient`
  // (per this project's colour-mode standard) — the old `bg.card` path is
  // invalid and silently falls back to a solid blue (#007AFF), which is what
  // made the Easy Read card render blue.
  const textPrimary = isEasyRead
    ? "#1A1A1A"
    : getColor(colorMode === "light" ? "text.primary" : "text.primaryDark", colorMode);
  const textMuted = isEasyRead
    ? "#3A3A3A"
    : getColor(colorMode === "light" ? "text.muted" : "text.mutedDark", colorMode);
  const cardBg = isEasyRead
    ? "#FFFFFF"
    : getColor("background.cardGradient", colorMode);
  const cardBorder = isEasyRead
    ? "#CBD5E0"
    : getColor("border.darkCard", colorMode);
  const fieldBg = isEasyRead ? "#FFFFFF" : getComponent("form", "fieldBg");
  const fieldBorder = isEasyRead ? "#4A5568" : getComponent("form", "fieldBorder");
  // Easy Read per-question block chrome.
  const erBlockBg = "#F7FAFC";
  const erBlockBorder = "#CBD5E0";

  // Inline wrapper: Container + Card for standalone, plain Box for embed.
  // Declared as a render function (not a component) so React doesn't remount
  // children on every re-render and drop input focus.
  const wrap = (children: React.ReactNode) =>
    embed ? (
      <Box w="100%">{children}</Box>
    ) : (
      <Container maxW={isEasyRead ? "3xl" : "lg"} py={{ base: 5, md: 10 }}>
        <Card
          bg={cardBg}
          borderColor={cardBorder}
          borderWidth={isEasyRead ? "2px" : "1px"}
          borderRadius={isEasyRead ? "xl" : "md"}
          boxShadow={isEasyRead ? "0 8px 30px rgba(0, 0, 0, 0.10)" : undefined}
          color={textPrimary}
        >
          <CardBody p={isEasyRead ? { base: 5, md: 10 } : undefined}>
            {children}
          </CardBody>
        </Card>
      </Container>
    );

  // Loading state
  if (loading) {
    return wrap(
      <VStack spacing={4} py={embed ? 4 : 8}>
        <Spinner size="xl" color="blue.500" />
        <Text color={textMuted}>Loading form...</Text>
      </VStack>
    );
  }

  // Error or not found
  if (error || !form) {
    return wrap(
      <VStack spacing={4} py={embed ? 4 : 8}>
        <Heading size="md" color={textPrimary}>
          Form Not Found
        </Heading>
        <Text color={textMuted} textAlign="center">
          This form is no longer available or the link may be incorrect.
        </Text>
      </VStack>
    );
  }

  // Success state
  if (submitted) {
    return wrap(
      <VStack spacing={4} py={embed ? 4 : 8}>
        {rewardAward && (
          <Box w="100%" maxW="md">
            <RewardCelebration count={rewardAward.count} tokenName={rewardAward.tokenName} />
          </Box>
        )}
        <Box fontSize="4xl">&#10003;</Box>
        <Heading size="md" color={textPrimary}>
          Submitted Successfully
        </Heading>
        <Text color={textMuted} textAlign="center" maxW="md">
          {form.thankYouMessage || "Thank you for your submission. We'll be in touch shortly."}
        </Text>
      </VStack>
    );
  }

  // Form render
  return wrap(
    <form onSubmit={handleSubmit}>
            <VStack spacing={isEasyRead ? 9 : 6} align="stretch">
              {!embed && (
                <Box>
                  <Heading
                    size={isEasyRead ? { base: "lg", md: "xl" } : "lg"}
                    color={textPrimary}
                    lineHeight="1.25"
                    mb={isEasyRead ? 4 : 2}
                  >
                    {form.name}
                  </Heading>
                  {isEasyRead && (
                    <Text fontSize={{ base: "md", md: "lg" }} color={textMuted} mb={5}>
                      Take your time. Every question marked optional can be skipped.
                    </Text>
                  )}
                  <Divider borderWidth={isEasyRead ? "1px" : undefined} />
                </Box>
              )}

              {fieldGroups.map((group, groupIndex) => (
                <React.Fragment key={groupIndex}>
                  {groupIndex > 0 && <Divider />}
                  <Box
                    display={isEasyRead ? "flex" : "grid"}
                    flexDirection={isEasyRead ? "column" : undefined}
                    gap={isEasyRead ? 5 : 4}
                    gridTemplateColumns={isEasyRead ? undefined : { base: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
                    w="100%"
                    alignItems="start"
                    sx={
                      isEasyRead
                        ? {
                            "& > .chakra-form-control": {
                              width: "100%",
                              padding: { base: 4, md: 5 },
                              background: erBlockBg,
                              border: "1px solid",
                              borderColor: erBlockBorder,
                              borderRadius: "12px",
                            },
                            "& input, & select, & textarea": { width: "100%" },
                          }
                        : undefined
                    }
                  >
                    {group.fields.map((field: any) => {
                      const fieldKey = field.name;
                      const value = formData[fieldKey] || "";

                      // "heading" — a display-only group label (no input). Lets a
                      // form show one heading above a block of checkboxes instead
                      // of repeating the context on every option's label.
                      if (field.type === "heading") {
                        return (
                          <FormControl key={fieldKey} gridColumn={{ md: "span 2" }}>
                            <Text
                              fontSize={isEasyRead ? { base: "xl", md: "2xl" } : "md"}
                              fontWeight="700"
                              lineHeight="1.35"
                              color={textPrimary}
                              mt={isEasyRead ? 4 : 2}
                            >
                              {field.label}
                            </Text>
                          </FormControl>
                        );
                      }

                      if (field.type === "textarea") {
                        return (
                          <FormControl
                            key={fieldKey}
                            isRequired={field.required}
                            gridColumn={{ md: "span 2" }}
                          >
                            <FormLabel
                              fontSize={isEasyRead ? { base: "lg", md: "xl" } : "sm"}
                              fontWeight={isEasyRead ? "semibold" : undefined}
                              lineHeight="1.4"
                              mb={isEasyRead ? 3 : 2}
                              color={textPrimary}
                            >
                              {field.label}
                            </FormLabel>
                            <Textarea
                              size={isEasyRead ? "lg" : "sm"}
                              minH={isEasyRead ? "140px" : undefined}
                              fontSize={isEasyRead ? "lg" : undefined}
                              value={value}
                              onChange={(e) => handleChange(fieldKey, e.target.value)}
                              placeholder={field.placeholder || ""}
                              bg={fieldBg}
                              color={textPrimary}
                              borderWidth={isEasyRead ? "2px" : "1px"}
                              borderColor={fieldBorder}
                            />
                          </FormControl>
                        );
                      }

                      if (field.type === "select") {
                        return (
                          <FormControl key={fieldKey} isRequired={field.required}>
                            <FormLabel
                              fontSize={isEasyRead ? { base: "lg", md: "xl" } : "sm"}
                              fontWeight={isEasyRead ? "semibold" : undefined}
                              lineHeight="1.4"
                              mb={isEasyRead ? 3 : 2}
                              color={textPrimary}
                            >
                              {field.label}
                            </FormLabel>
                            <Select
                              size={isEasyRead ? "lg" : "sm"}
                              minH={isEasyRead ? "56px" : undefined}
                              fontSize={isEasyRead ? "lg" : undefined}
                              value={value}
                              onChange={(e) => handleChange(fieldKey, e.target.value)}
                              bg={fieldBg}
                              color={textPrimary}
                              borderWidth={isEasyRead ? "2px" : "1px"}
                              borderColor={fieldBorder}
                            >
                              <option value="">Choose an answer...</option>
                              {(field.options || []).map((opt: string) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </Select>
                          </FormControl>
                        );
                      }

                      if (field.type === "checkbox") {
                        return (
                          <FormControl key={fieldKey} gridColumn={{ md: "span 2" }}>
                            <Checkbox
                              isChecked={value === "true"}
                              onChange={(e) =>
                                handleChange(fieldKey, e.target.checked ? "true" : "false")
                              }
                              colorScheme="green"
                              size={isEasyRead ? "lg" : "md"}
                              minH={isEasyRead ? "48px" : undefined}
                              spacing={isEasyRead ? 4 : undefined}
                            >
                              <Text
                                fontSize={isEasyRead ? { base: "lg", md: "xl" } : "sm"}
                                fontWeight={isEasyRead ? "medium" : undefined}
                                lineHeight="1.4"
                                color={textPrimary}
                              >
                                {field.label}
                              </Text>
                            </Checkbox>
                          </FormControl>
                        );
                      }

                      // text, email, phone
                      return (
                        <FormControl key={fieldKey} isRequired={field.required}>
                          <FormLabel
                            fontSize={isEasyRead ? { base: "lg", md: "xl" } : "sm"}
                            fontWeight={isEasyRead ? "semibold" : undefined}
                            lineHeight="1.4"
                            mb={isEasyRead ? 3 : 2}
                            color={textPrimary}
                          >
                            {field.label}
                          </FormLabel>
                          <Input
                            size={isEasyRead ? "lg" : "sm"}
                            minH={isEasyRead ? "56px" : undefined}
                            fontSize={isEasyRead ? "lg" : undefined}
                            type={field.type === "phone" ? "tel" : field.type === "email" ? "email" : "text"}
                            value={value}
                            onChange={(e) => handleChange(fieldKey, e.target.value)}
                            placeholder={field.placeholder || ""}
                            bg={fieldBg}
                            color={textPrimary}
                            borderWidth={isEasyRead ? "2px" : "1px"}
                            borderColor={fieldBorder}
                          />
                        </FormControl>
                      );
                    })}
                  </Box>
                </React.Fragment>
              ))}

              {submitError && (
                <Text color="red.500" fontSize="sm">
                  {submitError}
                </Text>
              )}

              <HelpTip label="Submit your details — we'll be in touch as soon as there's news. No card charged; you can unsubscribe any time.">
                <Button
                  type="submit"
                  colorScheme="green"
                  size="lg"
                  minH={isEasyRead ? "60px" : undefined}
                  fontSize={isEasyRead ? "xl" : undefined}
                  fontWeight="bold"
                  isLoading={submitting}
                  loadingText="Submitting..."
                  w="100%"
                >
                  {isEasyRead ? "Submit Feedback" : "Submit"}
                </Button>
              </HelpTip>
            </VStack>
          </form>
  );
};

export default PublicForm;
